import { useFocusEffect, useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import {
  Camera,
  Check,
  ChevronLeft,
  Plus,
  Receipt,
  RefreshCw,
  Search,
  Tag,
  Trash2,
  Upload,
  X,
} from 'lucide-react-native';
import { useCallback, useMemo, useState } from 'react';
import {
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';

import { useConfirm } from '@/components/ConfirmDialog';
import { InfiniteFooter } from '@/components/InfiniteFooter';
import { HScroll } from '@/components/HScroll';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import type { ExpenseRow, PropertyRow } from '@/db/types';
import { useInfiniteWindow } from '@/hooks/use-infinite-window';
import {
  addCustomExpenseCategory,
  allExpenseCategories,
  bulkDeleteExpenses,
  bulkSetExpenseCategory,
  type ExpenseTotals,
  getExpenseTotals,
  listExpenses,
} from '@/lib/expenses';
import { hapticHold, hapticSuccess } from '@/lib/haptics';
import { goBackOrTo } from '@/lib/nav';
import { getActiveProperty } from '@/lib/tasks';
import { webNoOutline } from '@/lib/web-style';

export default function ExpensesScreen() {
  const router = useRouter();
  const db = useSQLiteContext();
  const [items, setItems] = useState<ExpenseRow[]>([]);
  const [totals, setTotals] = useState<ExpenseTotals>({
    ytdCents: 0,
    last12Cents: 0,
    monthlyAvgCents: 0,
    byCategory: {},
    recurringMonthlyCents: 0,
    lifetimeCents: 0,
    prevYearSamePeriodCents: 0,
    byYear: [],
  });
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState<string | 'all'>('all');
  const [property, setProperty] = useState<PropertyRow | null>(null);
  // Mass edit (Wave 61): long-press a row to enter selection mode, tap
  // to toggle rows, then bulk-reassign category or bulk-delete from the
  // sticky action bar.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const selectMode = selected.size > 0;
  const [catPickerOpen, setCatPickerOpen] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const confirm = useConfirm();
  const insets = useSafeAreaInsets();

  const fetch = useCallback(async () => {
    const prop = await getActiveProperty(db);
    if (!prop) return;
    setProperty(prop);
    setItems(await listExpenses(db, prop.id));
    setTotals(await getExpenseTotals(db, prop.id));
  }, [db]);

  const allCats = useMemo(
    () => allExpenseCategories(property?.custom_expense_categories_json),
    [property?.custom_expense_categories_json]
  );

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const applyBulkCategory = async (cat: string) => {
    if (bulkBusy) return;
    setBulkBusy(true);
    try {
      await bulkSetExpenseCategory(db, [...selected], cat);
      hapticSuccess();
      setCatPickerOpen(false);
      clearSelection();
      await fetch();
    } finally {
      setBulkBusy(false);
    }
  };

  const applyBulkDelete = async () => {
    if (bulkBusy) return;
    const n = selected.size;
    const ok = await confirm({
      title: `Delete ${n} expense${n === 1 ? '' : 's'}?`,
      message: 'They disappear from totals and the annual rollup.',
      destructive: true,
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    setBulkBusy(true);
    try {
      await bulkDeleteExpenses(db, [...selected]);
      hapticSuccess();
      clearSelection();
      await fetch();
    } finally {
      setBulkBusy(false);
    }
  };

  const saveNewCategory = async () => {
    if (!property) return;
    const clean = await addCustomExpenseCategory(
      db,
      property.id,
      property.custom_expense_categories_json,
      newCatName
    );
    if (!clean) return;
    setNewCatName('');
    // Refresh the property row so the new name appears immediately,
    // then apply it to the selection in one motion.
    setProperty(await getActiveProperty(db));
    await applyBulkCategory(clean);
  };

  useFocusEffect(
    useCallback(() => {
      fetch();
    }, [fetch])
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((e) => {
      if (filterCat !== 'all' && e.category !== filterCat) return false;
      if (!q) return true;
      return (
        e.category.toLowerCase().includes(q) ||
        (e.vendor ?? '').toLowerCase().includes(q) ||
        (e.description ?? '').toLowerCase().includes(q)
      );
    });
  }, [items, search, filterCat]);

  // Render only the first slice of `filtered` and extend as the user
  // scrolls. The filter pass above runs against the full `items` array
  // so a search hit deep in the year is still found and surfaced.
  const win = useInfiniteWindow(filtered, { pageSize: 25, initialSize: 20 });

  // Sort categories by spend for the chip row
  const topCategories = useMemo(() => {
    return Object.entries(totals.byCategory)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 4)
      .map(([cat, cents]) => ({ cat, cents }));
  }, [totals.byCategory]);

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.topbar}>
        <Pressable onPress={() => goBackOrTo(router, '/(tabs)')} style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]} hitSlop={12}>
          <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
        </Pressable>
        <Text style={styles.topbarTitle}>Expenses</Text>
        <View style={{ flexDirection: 'row', gap: 6 }}>
          <Pressable
            onPress={() => router.push('/expenses/import-photos' as never)}
            style={({ pressed }) => [styles.addOutline, pressed && { opacity: 0.85 }]}
            accessibilityLabel="Scan receipts">
            <Camera size={16} color={Palette.forest} strokeWidth={2} />
          </Pressable>
          <Pressable
            onPress={() => router.push('/expenses/import' as never)}
            style={({ pressed }) => [styles.addOutline, pressed && { opacity: 0.85 }]}
            accessibilityLabel="Import CSV">
            <Upload size={16} color={Palette.forest} strokeWidth={2} />
          </Pressable>
          <Pressable
            onPress={() => router.push('/expenses/new' as never)}
            style={({ pressed }) => [styles.add, pressed && { opacity: 0.85 }]}>
            <Plus size={18} color={Palette.cream} strokeWidth={2.2} />
          </Pressable>
        </View>
      </View>

      <ScrollView
        contentContainerStyle={{ paddingBottom: 80 }}
        showsVerticalScrollIndicator={false}
        onScroll={win.onScroll}
        onLayout={win.onLayout}
        scrollEventThrottle={16}>
        {/* Headline stats */}
        <View style={styles.heroCard}>
          <View style={styles.heroLeft}>
            <Text style={styles.heroLabel}>SPENT THIS YEAR</Text>
            <Text style={styles.heroNum}>${(totals.ytdCents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}</Text>
            <Text style={styles.heroSub}>
              ${(totals.monthlyAvgCents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })} / mo avg
            </Text>
          </View>
          <View style={styles.heroRight}>
            <View style={styles.miniStat}>
              <Text style={styles.miniStatLabel}>LAST 12 MO</Text>
              <Text style={styles.miniStatValue}>
                ${(totals.last12Cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </Text>
            </View>
            <View style={styles.miniStat}>
              <View style={styles.miniStatRow}>
                <RefreshCw size={11} color={Palette.honeyDeep} strokeWidth={2} />
                <Text style={styles.miniStatLabel}>RECURRING / MO</Text>
              </View>
              <Text style={[styles.miniStatValue, { color: Palette.honeyDeep }]}>
                ${(totals.recurringMonthlyCents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </Text>
            </View>
          </View>
        </View>

        {/* Top categories */}
        {topCategories.length > 0 ? (
          <View style={styles.catBar}>
            {topCategories.map((c, i) => (
              <View key={c.cat} style={[styles.catBlock, i === 0 && styles.catBlockTop]}>
                <Text style={styles.catBlockLabel} numberOfLines={1}>{c.cat}</Text>
                <Text style={styles.catBlockValue}>${(c.cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}</Text>
              </View>
            ))}
          </View>
        ) : null}

        {totals.byYear.length > 0 ? (
          <View style={styles.rollupCard}>
            <View style={styles.rollupHead}>
              <Text style={styles.rollupTitle}>Annual rollup</Text>
              <Text style={styles.rollupLifetime}>
                Lifetime ${(totals.lifetimeCents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </Text>
            </View>
            {totals.byYear.length >= 2 ? (
              <View style={styles.yoyRow}>
                <YoyDelta
                  current={totals.ytdCents}
                  prev={totals.prevYearSamePeriodCents}
                />
              </View>
            ) : null}
            <HScroll contentStyle={styles.yearStrip}>
              {totals.byYear.slice(0, 6).map((y) => {
                const max = Math.max(...totals.byYear.map((x) => x.cents)) || 1;
                const pct = Math.max(8, Math.round((y.cents / max) * 100));
                return (
                  <View key={y.year} style={styles.yearBlock}>
                    <View style={styles.yearBarTrack}>
                      <View style={[styles.yearBarFill, { height: `${pct}%` }]} />
                    </View>
                    <Text style={styles.yearLabel}>{String(y.year).slice(2)}</Text>
                    <Text style={styles.yearValue}>
                      ${(y.cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </Text>
                  </View>
                );
              })}
            </HScroll>
          </View>
        ) : null}

        <View style={styles.searchBox}>
          <Search size={14} color={Palette.inkFaint} strokeWidth={1.8} />
          <TextInput
            value={search}
            onChangeText={setSearch}
            placeholder="Search vendor, category, note…"
            placeholderTextColor={Palette.inkFaint}
            style={[styles.searchInput, webNoOutline]}
          />
        </View>

        <HScroll contentStyle={styles.catRow}>
          <Pressable
            onPress={() => setFilterCat('all')}
            style={({ pressed }) => [styles.catChip, filterCat === 'all' && styles.catChipSel, pressed && { opacity: 0.9 }]}>
            <Text style={[styles.catChipText, filterCat === 'all' && styles.catChipTextSel]}>All</Text>
          </Pressable>
          {allCats.map((c) => {
            const sel = filterCat === c;
            return (
              <Pressable
                key={c}
                onPress={() => setFilterCat(c)}
                style={({ pressed }) => [styles.catChip, sel && styles.catChipSel, pressed && { opacity: 0.9 }]}>
                <Text style={[styles.catChipText, sel && styles.catChipTextSel]}>{c}</Text>
              </Pressable>
            );
          })}
        </HScroll>

        <Text style={styles.count}>
          {filtered.length} of {items.length} expense{items.length === 1 ? '' : 's'}
        </Text>

        {filtered.length === 0 ? (
          <View style={styles.empty}>
            <Receipt size={42} color={Palette.inkFaint} strokeWidth={1.4} />
            <Text style={styles.emptyTitle}>
              {items.length === 0 ? 'No expenses logged yet' : 'No matches'}
            </Text>
            <Text style={styles.emptyBody}>
              {items.length === 0
                ? 'Track utility bills, insurance premiums, property tax, and one-off projects. The total feeds your resale binder and your tax-prep folder.'
                : 'Try a different filter or clear the search.'}
            </Text>
            {items.length === 0 ? (
              <Pressable
                onPress={() => router.push('/expenses/new' as never)}
                style={({ pressed }) => [styles.emptyCta, pressed && { transform: [{ scale: 0.98 }] }]}>
                <Plus size={16} color={Palette.cream} strokeWidth={2} />
                <Text style={styles.emptyCtaText}>Log your first expense</Text>
              </Pressable>
            ) : null}
          </View>
        ) : (
          <View style={styles.list}>
            {win.visible.map((e) => (
              <Pressable
                key={e.id}
                onPress={() =>
                  selectMode
                    ? toggleSelect(e.id)
                    : router.push(`/expenses/${e.id}` as never)
                }
                onLongPress={() => {
                  if (!selectMode) hapticHold();
                  toggleSelect(e.id);
                }}
                delayLongPress={280}
                style={({ pressed }) => [
                  styles.row,
                  selected.has(e.id) && styles.rowSelected,
                  pressed && { transform: [{ scale: 0.99 }] },
                ]}>
                <View style={[styles.rowIcon, selected.has(e.id) && styles.rowIconSel]}>
                  {selected.has(e.id) ? (
                    <Check size={20} color={Palette.cream} strokeWidth={2.4} />
                  ) : (
                    <Receipt size={20} color={Palette.forest} strokeWidth={1.7} />
                  )}
                </View>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={styles.rowTitle} numberOfLines={1}>
                    {e.vendor ?? e.description ?? e.category}
                  </Text>
                  <Text style={styles.rowMeta} numberOfLines={1}>
                    {e.category}
                    {e.recurring ? ` · ${e.recurring}` : ''}
                    {' · '}
                    {new Date(e.paid_on).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                  </Text>
                </View>
                <Text style={styles.rowValue}>
                  ${(e.amount_cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Text>
              </Pressable>
            ))}
            <InfiniteFooter hasMore={win.hasMore} total={win.total} loadMore={win.loadMore} noun="expense" />
          </View>
        )}
      </ScrollView>

      {/* Mass-edit action bar — floats over the list while rows are
          selected. Category opens the picker (with "add your own"),
          Delete confirms, X clears the selection. */}
      {selectMode ? (
        <View style={[styles.bulkBar, { bottom: Math.max(14, insets.bottom + 10) }]}>
          <Text style={styles.bulkCount}>{selected.size} selected</Text>
          <Pressable
            onPress={() => setCatPickerOpen(true)}
            disabled={bulkBusy}
            style={({ pressed }) => [styles.bulkBtn, pressed && { opacity: 0.85 }]}>
            <Tag size={14} color={Palette.cream} strokeWidth={2} />
            <Text style={styles.bulkBtnText}>Category</Text>
          </Pressable>
          <Pressable
            onPress={applyBulkDelete}
            disabled={bulkBusy}
            style={({ pressed }) => [styles.bulkBtn, styles.bulkBtnDanger, pressed && { opacity: 0.85 }]}>
            <Trash2 size={14} color={Palette.cream} strokeWidth={2} />
            <Text style={styles.bulkBtnText}>Delete</Text>
          </Pressable>
          <Pressable
            onPress={clearSelection}
            hitSlop={8}
            accessibilityLabel="Clear selection"
            style={({ pressed }) => [styles.bulkClose, pressed && { opacity: 0.8 }]}>
            <X size={16} color={Palette.cream} strokeWidth={2.2} />
          </Pressable>
        </View>
      ) : null}

      {/* Bulk category picker — built-ins + the user's own categories,
          plus an inline "create one" row that saves and applies. */}
      <Modal transparent visible={catPickerOpen} animationType="fade" onRequestClose={() => setCatPickerOpen(false)}>
        <Pressable style={styles.modalBackdrop} onPress={() => setCatPickerOpen(false)}>
          <Pressable style={styles.modalCard} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.modalTitle}>
              Move {selected.size} expense{selected.size === 1 ? '' : 's'} to…
            </Text>
            <ScrollView style={{ maxHeight: 360 }} showsVerticalScrollIndicator={false}>
              {allCats.map((c) => (
                <Pressable
                  key={c}
                  onPress={() => void applyBulkCategory(c)}
                  disabled={bulkBusy}
                  style={({ pressed }) => [styles.modalRow, pressed && { backgroundColor: '#EEF3EE' }]}>
                  <Text style={styles.modalRowText}>{c}</Text>
                </Pressable>
              ))}
            </ScrollView>
            <View style={styles.newCatRow}>
              <TextInput
                value={newCatName}
                onChangeText={setNewCatName}
                placeholder="Or create your own…"
                placeholderTextColor={Palette.inkFaint}
                style={[styles.newCatInput, webNoOutline]}
                onSubmitEditing={() => void saveNewCategory()}
              />
              <Pressable
                onPress={() => void saveNewCategory()}
                disabled={!newCatName.trim() || bulkBusy}
                style={({ pressed }) => [
                  styles.newCatBtn,
                  (!newCatName.trim() || bulkBusy) && { opacity: 0.45 },
                  pressed && { opacity: 0.85 },
                ]}>
                <Plus size={15} color={Palette.cream} strokeWidth={2.2} />
              </Pressable>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </SafeAreaView>
  );
}

function YoyDelta({ current, prev }: { current: number; prev: number }) {
  if (prev <= 0) {
    return (
      <Text style={styles.yoyNeutral}>
        No same-period total for last year — once this year ends you&apos;ll see year-over-year here.
      </Text>
    );
  }
  const diff = current - prev;
  const pct = Math.round((diff / prev) * 100);
  const up = diff > 0;
  return (
    <Text style={[styles.yoyText, up ? styles.yoyUp : styles.yoyDown]}>
      {up ? '▲' : '▼'} {Math.abs(pct)}% vs. same period last year ($
      {(Math.abs(diff) / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}{' '}
      {up ? 'more' : 'less'})
    </Text>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: Spacing.four, paddingTop: Spacing.two, paddingBottom: Spacing.two,
  },
  back: {
    width: 36, height: 36, borderRadius: 18, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  add: {
    width: 36, height: 36, borderRadius: 18,
    backgroundColor: Palette.forest,
    alignItems: 'center', justifyContent: 'center',
  },
  addOutline: {
    width: 36, height: 36, borderRadius: 18,
    backgroundColor: Palette.card,
    borderWidth: 1, borderColor: Palette.line,
    alignItems: 'center', justifyContent: 'center',
  },
  heroCard: {
    flexDirection: 'row',
    marginHorizontal: Spacing.four,
    marginTop: Spacing.three,
    borderRadius: Radius.xl,
    backgroundColor: Palette.forest,
    padding: 16,
    gap: 16,
  },
  heroLeft: { flex: 1.4 },
  heroLabel: {
    fontFamily: Fonts.sansBold,
    fontSize: 11,
    color: 'rgba(251,246,234,0.7)',
    letterSpacing: 0.7,
  },
  heroNum: {
    fontFamily: Fonts.serifBold,
    fontSize: 34,
    color: Palette.cream,
    letterSpacing: -0.8,
    marginTop: 2,
    lineHeight: 38,
  },
  heroSub: {
    fontFamily: Fonts.sansSemibold,
    fontSize: 13.5,
    color: 'rgba(251,246,234,0.8)',
    marginTop: 4,
  },
  heroRight: { flex: 1, gap: 8 },
  miniStat: {
    backgroundColor: 'rgba(251,246,234,0.1)',
    borderRadius: 10,
    padding: 10,
  },
  miniStatRow: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  miniStatLabel: {
    fontFamily: Fonts.sansBold,
    fontSize: 10.5,
    color: 'rgba(251,246,234,0.65)',
    letterSpacing: 0.5,
  },
  miniStatValue: {
    fontFamily: Fonts.sansBold,
    fontSize: 16,
    color: Palette.cream,
    marginTop: 2,
  },
  catBar: {
    flexDirection: 'row',
    marginHorizontal: Spacing.four,
    marginTop: 10,
    gap: 6,
  },
  catBlock: {
    flex: 1,
    backgroundColor: Palette.card,
    borderWidth: 1, borderColor: Palette.line,
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  catBlockTop: { borderColor: Palette.honey, backgroundColor: '#FBEFD3' },
  catBlockLabel: {
    fontFamily: Fonts.sansBold,
    fontSize: 11,
    color: Palette.inkSoft,
    letterSpacing: 0.3,
  },
  catBlockValue: {
    fontFamily: Fonts.sansBold,
    fontSize: 14,
    color: Palette.ink,
    marginTop: 2,
  },
  searchBox: {
    flexDirection: 'row', alignItems: 'center', gap: 7,
    marginHorizontal: Spacing.four, marginTop: Spacing.three,
    backgroundColor: Palette.card, borderWidth: 1.5,
    borderColor: Palette.line, borderRadius: 11,
    paddingHorizontal: 11,
  },
  searchInput: {
    flex: 1, paddingVertical: 10,
    fontFamily: Fonts.sansSemibold, fontSize: 13, color: Palette.ink,
  },
  catRow: {
    paddingHorizontal: Spacing.four,
    marginTop: 10,
    gap: 6,
  },
  catChip: {
    paddingHorizontal: 12, paddingVertical: 6, borderRadius: 999,
    borderWidth: 1.5, borderColor: Palette.line, backgroundColor: Palette.card,
    marginRight: 6,
  },
  catChipSel: { backgroundColor: Palette.forest, borderColor: Palette.forest },
  catChipText: { fontFamily: Fonts.sansBold, fontSize: 13, color: Palette.ink },
  catChipTextSel: { color: Palette.cream },
  count: {
    marginHorizontal: Spacing.four, marginTop: 14,
    fontFamily: Fonts.sansSemibold, fontSize: 13, color: Palette.inkFaint,
  },
  empty: {
    alignItems: 'center', paddingHorizontal: 32, paddingVertical: 56, gap: 12,
  },
  emptyTitle: { fontFamily: Fonts.serifBold, fontSize: 18, color: Palette.ink },
  emptyBody: {
    fontFamily: Fonts.sansMedium, fontSize: 13, color: Palette.inkSoft,
    textAlign: 'center', lineHeight: 19,
  },
  emptyCta: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: Palette.forest,
    paddingHorizontal: 16, paddingVertical: 11, borderRadius: 14,
  },
  emptyCtaText: { color: Palette.cream, fontFamily: Fonts.sansBold, fontSize: 13.5 },
  list: { paddingHorizontal: 18, marginTop: 8, gap: 8 },
  row: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    backgroundColor: Palette.card, borderWidth: 1, borderColor: Palette.line,
    borderRadius: 14, padding: 12,
  },
  rowIcon: {
    width: 40, height: 40, borderRadius: 12,
    backgroundColor: '#EAF0EA',
    alignItems: 'center', justifyContent: 'center',
  },
  rowTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  rowMeta: {
    fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.inkSoft, marginTop: 2,
  },
  rowValue: { fontFamily: Fonts.sansBold, fontSize: 15, color: Palette.honeyDeep },
  rowSelected: { borderColor: Palette.forest, backgroundColor: '#EEF3EE' },
  rowIconSel: { backgroundColor: Palette.forest },
  bulkBar: {
    position: 'absolute', left: 14, right: 14,
    flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: Palette.forest, borderRadius: 16,
    paddingHorizontal: 14, paddingVertical: 12,
    shadowColor: '#000', shadowOpacity: 0.22, shadowRadius: 12,
    shadowOffset: { width: 0, height: 4 }, elevation: 8,
  },
  bulkCount: {
    flex: 1, fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.cream,
  },
  bulkBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 5,
    backgroundColor: 'rgba(251,246,234,0.16)',
    paddingHorizontal: 12, paddingVertical: 8, borderRadius: 10,
  },
  bulkBtnDanger: { backgroundColor: 'rgba(176,95,60,0.55)' },
  bulkBtnText: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.cream },
  bulkClose: { padding: 6 },
  modalBackdrop: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.45)',
    alignItems: 'center', justifyContent: 'center', padding: 20,
  },
  modalCard: {
    backgroundColor: Palette.sand, borderRadius: 24,
    padding: 18, width: '100%', maxWidth: 380,
  },
  modalTitle: {
    fontFamily: Fonts.serifBold, fontSize: 17, color: Palette.ink, marginBottom: 10,
  },
  modalRow: { paddingVertical: 11, paddingHorizontal: 12, borderRadius: 10 },
  modalRowText: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.ink },
  newCatRow: {
    flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 10,
  },
  newCatInput: {
    flex: 1, borderWidth: 1.5, borderColor: Palette.line,
    backgroundColor: Palette.card, borderRadius: 11,
    paddingHorizontal: 12, paddingVertical: 10,
    fontFamily: Fonts.sansSemibold, fontSize: 13.5, color: Palette.ink,
  },
  newCatBtn: {
    width: 40, height: 40, borderRadius: 12,
    backgroundColor: Palette.forest,
    alignItems: 'center', justifyContent: 'center',
  },
  rollupCard: {
    marginTop: Spacing.three, marginHorizontal: 18,
    backgroundColor: Palette.card,
    borderRadius: Radius.xl,
    borderWidth: 1, borderColor: Palette.line,
    padding: 14,
  },
  rollupHead: {
    flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between',
    marginBottom: 6,
  },
  rollupTitle: {
    fontFamily: Fonts.serifBold, fontSize: 15, color: Palette.ink, letterSpacing: -0.2,
  },
  rollupLifetime: {
    fontFamily: Fonts.sansBold, fontSize: 12.5, color: Palette.inkSoft, letterSpacing: 0.2,
  },
  yoyRow: { marginBottom: 8 },
  yoyText: { fontFamily: Fonts.sansBold, fontSize: 13, letterSpacing: 0.2 },
  yoyUp: { color: Palette.clay },
  yoyDown: { color: Palette.moss },
  yoyNeutral: {
    fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.inkSoft, lineHeight: 16,
  },
  yearStrip: {
    flexDirection: 'row', gap: 14, paddingVertical: 4, alignItems: 'flex-end',
  },
  yearBlock: { alignItems: 'center', width: 44 },
  yearBarTrack: {
    width: 18, height: 56, borderRadius: 6,
    backgroundColor: Palette.sand, justifyContent: 'flex-end',
    overflow: 'hidden',
  },
  yearBarFill: { backgroundColor: Palette.forest, width: '100%', borderRadius: 6 },
  yearLabel: {
    marginTop: 5, fontFamily: Fonts.sansBold, fontSize: 12, color: Palette.inkSoft,
  },
  yearValue: {
    marginTop: 2, fontFamily: Fonts.sansSemibold, fontSize: 11.5, color: Palette.inkFaint,
  },
});
